import type { PageGroups, PaginationItem } from 'components/common/Pagination/Pagination.types';

/**
 * Returns the ordered page numbers to display, e.g. `[1, 4, 5, 6, 10]`.
 * The `…` ellipsis is not included here — it's derived in `createPaginationItems` from the gaps between these numbers.
 * Returns `[]` when there is nothing to paginate.
 *
 * @param currentPage - the active page, 1-based
 * @param totalPages - total number of pages
 */
export const buildPageNumbers = (currentPage: number, totalPages: number): number[] => {
  if (!totalPages || !(totalPages > 1)) {
    return [];
  }

  const pages: number[] = [];
  if (totalPages <= 4) {
    for (let i = 0; i < totalPages; i++) {
      pages.push(i + 1);
    }
  } else if (currentPage > 3 && currentPage <= totalPages - 3) {
    pages.push(1, currentPage - 2, currentPage - 1, currentPage, currentPage + 1, totalPages);
  } else if (currentPage > totalPages - 3) {
    pages.push(1);
    for (let i = totalPages - 3; i <= totalPages; i++) {
      pages.push(i);
    }
  } else {
    for (let i = 0; i < 4; i++) {
      pages.push(i + 1);
    }
    pages.push(totalPages);
  }

  return pages;
};

/**
 * Builds the ordered pagination items to render — page tokens interleaved with `…` ellipsis tokens,
 * e.g. `[1, …, 4, 5, 6, …, 10]`. Wraps `buildPageNumbers` and inserts ellipsis tokens using the
 * exact index-based rules from the legacy makeDesktopPages logic, so the rendered sequence is
 * unchanged. Returns `[]` when there is nothing to paginate.
 *
 * @param currentPage - the active page, 1-based
 * @param totalPages - total number of pages
 */
export const createPaginationItems = (currentPage: number, totalPages: number): PaginationItem[] => {
  const pages = buildPageNumbers(currentPage, totalPages);
  const items: PaginationItem[] = [];

  pages.forEach((page, i) => {
    if (i === 0 && currentPage > 4 && totalPages > 5) {
      // Leading truncation: first page, then `…`.
      items.push({ type: 'page', page }, { type: 'ellipsis' });
    } else if (pages.length - 1 === i && currentPage < totalPages - 3) {
      // Trailing truncation: `…`, then the last page.
      items.push({ type: 'ellipsis' }, { type: 'page', page });
    } else {
      items.push({ type: 'page', page });
    }
  });

  return items;
};

/**
 * Windowing used by Order History pagination — a tighter 3-at-a-time window returned as up to three
 * groups (the gaps between groups become `…` ellipses at render time). 1-based; `currentPage` comes
 * from the page query param.
 *
 * @param totalPages - total number of pages
 * @param currentPage - the active page, 1-based
 */
export const createPageGroups = (totalPages: number, currentPage: number): PageGroups => {
  const pageGroup1: number[] = [];
  const pageGroup2: number[] = [];
  const pageGroup3: number[] = [];

  if (totalPages <= 3) {
    for (let i = 1; i <= totalPages; i++) {
      pageGroup1.push(i);
    }
  } else if (totalPages === 4) {
    if (currentPage <= 2) {
      pageGroup1.push(1, 2, 3);
      pageGroup2.push(4);
    } else {
      pageGroup1.push(1);
      pageGroup2.push(2, 3, 4);
    }
  } else {
    if (currentPage <= 2) {
      pageGroup1.push(1, 2, 3);
      pageGroup2.push(totalPages);
    } else if (currentPage >= totalPages - 1) {
      pageGroup1.push(1);
      pageGroup2.push(totalPages - 2, totalPages - 1, totalPages);
    } else {
      pageGroup1.push(1);
      pageGroup2.push(currentPage - 1, currentPage, currentPage + 1);
      pageGroup3.push(totalPages);
    }
  }

  return { pageGroup1, pageGroup2, pageGroup3 };
};

/**
 * Flattens `createPageGroups` output into the shared `PaginationItem[]` shape, inserting an ellipsis
 * token only where a real numeric gap exists between adjacent groups. This drops the legacy quirk of
 * always prefixing `…` (which could render e.g. `1 2 3 … 4` between consecutive pages).
 */
export const pageGroupsToItems = ({ pageGroup1, pageGroup2, pageGroup3 }: PageGroups): PaginationItem[] => {
  const items: PaginationItem[] = [];

  [pageGroup1, pageGroup2, pageGroup3].forEach(group => {
    const firstPage = group[0];
    if (firstPage === undefined) {
      return;
    }
    const prev = items[items.length - 1];
    // Bridge a numeric gap between the previous page token and this group's first page with `…`.
    if (prev && prev.type === 'page' && firstPage > prev.page + 1) {
      items.push({ type: 'ellipsis' });
    }
    group.forEach(page => items.push({ type: 'page', page }));
  });

  return items;
};
